import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #boundOnSellingPlanChange = this.#onSellingPlanChange.bind(this);
  #boundOnUpgradeToSubscription = this.#onUpgradeToSubscription.bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('change', this.#boundOnSellingPlanChange);
    this.addEventListener('click', this.#boundOnUpgradeToSubscription);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('change', this.#boundOnSellingPlanChange);
    this.removeEventListener('click', this.#boundOnUpgradeToSubscription);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartItemRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles selling plan selector changes for existing subscription lines.
   * @param {Event} event
   */
  #onSellingPlanChange(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!event.target.matches('.cart-items__selling-plan-selector')) return;

    const line = Number.parseInt(event.target.dataset.line || '', 10);
    const quantity = Number.parseInt(event.target.dataset.quantity || '1', 10);
    const sellingPlanId = event.target.value || '';

    if (!Number.isFinite(line) || line <= 0 || !sellingPlanId) return;

    this.updateLineItem({
      line,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      sellingPlanId,
      action: 'change-selling-plan',
    });
  }

  /**
   * Handles upgrade button clicks for one-time lines.
   * @param {MouseEvent} event
   */
  #onUpgradeToSubscription(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('.cart-items__upgrade-button');
    if (!(button instanceof HTMLButtonElement)) return;

    const line = Number.parseInt(button.dataset.line || '', 10);
    const quantity = Number.parseInt(button.dataset.quantity || '1', 10);
    const defaultSellingPlanId = button.dataset.defaultSellingPlanId || '';

    if (!Number.isFinite(line) || line <= 0 || !defaultSellingPlanId) return;

    this.updateLineItem({
      line,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      sellingPlanId: defaultSellingPlanId,
      action: 'upgrade-to-subscription',
    });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    if (cartItemRowToRemove.classList.contains('cart-items__bundle-parent')) {
      this.#removeBundleGroup(cartItemRowToRemove, line);
      return;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      ...(this.refs.cartItemRows ?? []).filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    for (const row of rowsToRemove) {
      this.#animateRowRemoval(row);
    }
  }

  /**
   * Removes a bundle parent and all related bundle lines using cart/update.
   * @param {HTMLTableRowElement} bundleParentRow
   * @param {number} line
   */
  async #removeBundleGroup(bundleParentRow, line) {
    const bundleGroupKey = bundleParentRow.dataset.bundleGroupKey;
    if (!bundleGroupKey) {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
      return;
    }

    const relatedRows = (this.refs.cartItemRows ?? []).filter(
      (row) => row.dataset.bundleGroupKey === bundleGroupKey
    );
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear-bundle:user-action');

    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    for (const row of relatedRows) {
      this.#animateRowRemoval(row);
    }

    try {
      const updates = await this.#getBundleUpdates(bundleGroupKey, bundleParentRow);

      if (!Object.keys(updates).length) {
        this.updateQuantity({
          line,
          quantity: 0,
          action: 'clear',
        });
        return;
      }

      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({
            updates,
            sections: this.#getSectionsToUpdate().join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const parsedResponseText = JSON.parse(await response.text());

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        return;
      }

      this.#applyCartResponse(parsedResponseText);
    } catch (error) {
      console.error(error);
      this.#restoreCartSection();
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Builds quantity updates for every cart line that shares a bundle id.
   * @param {string} bundleGroupKey
   * @param {HTMLTableRowElement} bundleParentRow
   * @returns {Promise<Record<string, number>>}
   */
  async #getBundleUpdates(bundleGroupKey, bundleParentRow) {
    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    const cart = await cartResponse.json();
    const updates = {};

    if (Array.isArray(cart.items)) {
      for (const item of cart.items) {
        const itemBundleId = item.properties?._bundle_id;

        if (itemBundleId != null && String(itemBundleId) === String(bundleGroupKey) && item.key) {
          updates[item.key] = 0;
        }
      }
    }

    if (bundleParentRow.dataset.key) {
      updates[bundleParentRow.dataset.key] = 0;
    }

    for (const row of this.refs.cartItemRows ?? []) {
      if (row.dataset.bundleGroupKey === bundleGroupKey && row.dataset.key) {
        updates[row.dataset.key] = 0;
      }
    }

    return updates;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.updateLineItem(config);
  }

  /**
   * Updates a cart line item (quantity and/or selling plan).
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} [config.sellingPlanId]
   * @param {string} config.action
   */
  async updateLineItem(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const lineItemRow = this.#getCartItemRow(line);
    /** @type {Record<string, unknown>} */
    const payload = {
      quantity,
      sections: this.#getSectionsToUpdate().join(','),
      sections_url: window.location.pathname,
    };

    if (lineItemRow?.dataset.key) {
      payload.id = lineItemRow.dataset.key;
    } else {
      payload.line = line;
    }

    if (config.sellingPlanId) {
      payload.selling_plan = config.sellingPlanId;
    }

    cartTotal?.shimmer();

    try {
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const parsedResponseText = JSON.parse(await response.text());

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        return;
      }

      this.#applyCartResponse(parsedResponseText);
    } catch (error) {
      console.error(error);
      this.#restoreCartSection();
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Finds a cart row by 1-based line index after grouping reorders the DOM.
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartItemRow(line) {
    return this.refs.cartItemRows?.find((row) => Number.parseInt(row.dataset.line || '', 10) === line);
  }

  #getQuantitySelector(line) {
    return this.refs.quantitySelectors?.find((selector) => {
      const input = selector.querySelector('[data-cart-line]');
      return Number.parseInt(input?.dataset.cartLine || '', 10) === line;
    });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelector(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
      return;
    }

    this.#restoreCartSection();
  };

  /**
   * Morphs the cart section from a cart API sections payload.
   * @param {{ sections?: Record<string, string> }} parsedResponseText
   */
  #applyCartResponse(parsedResponseText) {
    const sectionHtml = parsedResponseText.sections?.[this.sectionId];
    const newSectionHTML = new DOMParser().parseFromString(sectionHtml || '', 'text/html');
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    if (sectionHtml) {
      morphSection(this.sectionId, sectionHtml);
    } else {
      this.#restoreCartSection();
    }
  }

  #restoreCartSection() {
    resetShimmer(this);
    sectionRenderer.renderSection(this.sectionId, { cache: false });
  }

  /**
   * @param {HTMLTableRowElement} row
   */
  #animateRowRemoval(row) {
    const remove = () => row.remove();

    if (prefersReducedMotion()) {
      remove();
      return;
    }

    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('removing');
    onAnimationEnd(row, remove);
  }

  /**
   * @returns {string[]}
   */
  #getSectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);

    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    }

    return Array.from(sectionsToUpdate);
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
